import type {
    ReservationCheckInStatusHistory} from "@@/shared/statuses/reservation-check-ins/reservation-check-in-status-history";
import {
    reservationCheckInStatusHistoryFactory,
} from "@@/shared/statuses/reservation-check-ins/reservation-check-in-status-history";
import type { StatusEvent } from "@@/shared/statuses/status-event";
import type { StatusId } from "@@/shared/statuses/status-id";
import type {
    StatusReferences} from "@@/shared/statuses/status-references";
import {
    isStatusReferenceMatch,
} from "@@/shared/statuses/status-references";
import { exists } from "@@/utilities/exists";
import { sortBy } from "@@/utilities/sort";

const statusReferencesToCheckInHistoryId = (
    references: StatusReferences,
): StatusId => {
    return `status__check_in_history__${Object.entries(references)
        .sort(sortBy(([key]) => key))
        .map(([_, value]) => value)
        .join("_")}` as StatusId;
};

function reservationCheckInStatusHistoryReducer(
    events: StatusEvent[],
    statusFor: StatusReferences,
) {
    const eventsFiltered = events.filter(event =>
        isStatusReferenceMatch(statusFor, event.references),
    );

    // Create initial status
    const projection: ReservationCheckInStatusHistory =
        reservationCheckInStatusHistoryFactory({
            _id: statusReferencesToCheckInHistoryId(statusFor),
            references: statusFor,
            statuses: [],
        });

    // If no events and no initial status is provided, throw an error
    if (!eventsFiltered.length) {
        return projection;
    }

    // Set initial status
    // Set it to the provided status or if no initial status is provided,
    // since we know there are events, create an initial status
    const statuses = eventsFiltered
        .sort(sortBy(event => event._createdAt.iso))
        .map(event => {
            switch (event.type) {
                case "CHECK_IN__CHECKED_IN":
                    return {
                        setAt: event._createdAt,
                        status: "CHECKED_IN",
                        setBy: event._createdBy,
                        message: event.payload?.message,
                    } satisfies ReservationCheckInStatusHistory["statuses"][0];
                case "CHECK_IN__CHECKED_OUT":
                    return {
                        setAt: event._createdAt,
                        status: "CHECKED_OUT",
                        setBy: event._createdBy,
                        message: event.payload?.message,
                    } satisfies ReservationCheckInStatusHistory["statuses"][0];
                case "CHECK_IN__INITIATED":
                case "CHECK_IN__PENDING":
                    return {
                        setAt: event._createdAt,
                        status: "PENDING",
                        setBy: event._createdBy,
                        message: event.payload?.message,
                    } satisfies ReservationCheckInStatusHistory["statuses"][0];
                default:
                    return undefined;
            }
        })
        .filter(exists);

    projection.statuses = statuses;
    return projection;
}
export { reservationCheckInStatusHistoryReducer };
